import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { getAccounts, getBalance, getAllCvus } from '../services/accountService'
import { listCVUs } from '../services/cvuService'
import { exportToCSV, exportToExcel, exportToPDF } from '../utils/exportUtils'
import { Link } from 'react-router-dom'
import { LuWallet, LuDownload, LuFileSpreadsheet, LuSheet, LuFileText, LuPrinter, LuRefreshCw, LuSearch, LuX, LuIdCard } from 'react-icons/lu'
import './AccountsPage.css'

function AccountsPage() {
  const { userRole, roleReady, isPortalUser, assignedAccounts } = useAuth()
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showExportMenu, setShowExportMenu] = useState(false)
  
  // Estados para CREDENCIAL - Consulta de saldo
  const [accountNumber, setAccountNumber] = useState('')
  const [balanceData, setBalanceData] = useState(null)
  const [balanceLoading, setBalanceLoading] = useState(false)
  const [showBlockedModal, setShowBlockedModal] = useState(false)
  const [showBadRequestModal, setShowBadRequestModal] = useState(false)
  const [allAccountsForSelect, setAllAccountsForSelect] = useState([])
  const [accountsSelectLoading, setAccountsSelectLoading] = useState(false)
  
  // Estados para selección múltiple de cuentas
  const [selectedAccounts, setSelectedAccounts] = useState([])
  const [multipleBalances, setMultipleBalances] = useState({}) // { account_number: { balance, loading, error } }
  const [showAccountsDropdown, setShowAccountsDropdown] = useState(false)
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 })

  // Estados para BIND - CVUs dados de alta
  const [bindCVUs, setBindCVUs] = useState([])
  const [cvusLoading, setCvusLoading] = useState(false)

  useEffect(() => {
    if (userRole !== 'CREDENCIAL') {
      loadAccounts()
      loadCVUs() // Cargar CVUs dados de alta para BIND
    } else {
      setLoading(false)
      // Si es usuario del portal, seleccionar la primera cuenta por defecto
      if (isPortalUser && assignedAccounts && assignedAccounts.length > 0) {
        setAccountNumber(assignedAccounts[0].account_number)
      }
      // Si es operador CREDENCIAL, cargar todas las cuentas para el selector
      if (!isPortalUser) {
        loadAllAccountsForSelect()
      }
    }
    
    // Cerrar dropdown al hacer clic fuera
    const handleClickOutside = (event) => {
      if (!event.target.closest('.export-dropdown')) {
        setShowExportMenu(false)
      }
    }
    
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [userRole, isPortalUser, assignedAccounts])

  // Cargar todas las cuentas para el selector (operador CREDENCIAL)
  const loadAllAccountsForSelect = async () => {
    try {
      setAccountsSelectLoading(true)
      const response = await getAllCvus()
      setAllAccountsForSelect(response.cvus || [])
    } catch (err) {
      console.error('Error al cargar cuentas:', err)
      setAllAccountsForSelect([])
    } finally {
      setAccountsSelectLoading(false)
    }
  }

  const loadAccounts = async () => {
    try {
      setLoading(true)
      setError('')
      
      const response = await getAccounts()
      setAccounts(response.data || [])
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al cargar cuentas'
      const httpCode = err.response?.data?.httpCode || err.response?.status || 500
      setError(`HTTP ${httpCode} - ${errorMsg}`)
    } finally {
      setLoading(false)
    }
  }

  // Cargar CVUs dados de alta (BIND)
  const loadCVUs = async () => {
    try {
      setCvusLoading(true)
      const response = await listCVUs()
      setBindCVUs(response.data || [])
    } catch (err) {
      console.error('Error al cargar CVUs:', err)
      // No mostramos error al usuario, simplemente no se muestran CVUs
      setBindCVUs([])
    } finally {
      setCvusLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount)
  }

  const handleExportCSV = () => {
    const data = accounts.map(acc => ({
      'Alias': acc.label,
      'Número': acc.number,
      'Tipo': acc.type,
      'Saldo': acc.balance.amount,
      'CBU': acc.account_routing.address,
      'Estado': acc.status
    }))
    exportToCSV(data, 'cuentas_flash')
    setShowExportMenu(false)
  }

  const handleExportExcel = () => {
    const data = accounts.map(acc => ({
      'Alias': acc.label,
      'Número': acc.number,
      'Tipo': acc.type,
      'Saldo': acc.balance.amount,
      'CBU': acc.account_routing.address,
      'Estado': acc.status
    }))
    exportToExcel(data, 'cuentas_flash')
    setShowExportMenu(false)
  }

  const handleExportPDF = () => {
    const columns = ['Alias', 'Número', 'Tipo', 'Saldo', 'CBU', 'Estado']
    const data = accounts.map(acc => [
      acc.label,
      acc.number,
      acc.type,
      formatCurrency(acc.balance.amount),
      acc.account_routing.address,
      acc.status
    ])
    exportToPDF('Cuentas - Flash', columns, data, 'cuentas_flash')
    setShowExportMenu(false)
  }

  const handlePrint = () => {
    window.print()
    setShowExportMenu(false)
  }

  // Consultar saldo (CREDENCIAL) - Single account (para portal users)
  const handleConsultarSaldo = async (e) => {
    e.preventDefault()
    if (!accountNumber.trim()) {
      setError('Seleccione una cuenta')
      return
    }

    // Validar que el usuario del portal solo consulte sus CVUs asignados
    if (isPortalUser) {
      const isAssigned = assignedAccounts.some(acc => acc.account_number === accountNumber)
      if (!isAssigned) {
        setError('No tiene permiso para consultar esta cuenta')
        return
      }
    }

    setBalanceLoading(true)
    setError('')
    setBalanceData(null)

    try {
      const response = await getBalance(accountNumber.trim())
      setBalanceData(response)
      
      // Verificar si la cuenta está bloqueada
      if (response.description === 'blocked account') {
        setShowBlockedModal(true)
      }
      
      // Verificar si hay bad request
      if (response.description === 'bad request') {
        setShowBadRequestModal(true)
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al consultar saldo'
      const httpCode = err.response?.data?.httpCode || err.response?.status || 500
      setError(`HTTP ${httpCode} - ${errorMsg}`)
    } finally {
      setBalanceLoading(false)
    }
  }

  // Toggle selección de cuenta individual
  const handleToggleAccount = (accountNum) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountNum)) {
        return prev.filter(acc => acc !== accountNum)
      } else {
        return [...prev, accountNum]
      }
    })
  }

  // Obtener las cuentas disponibles según el tipo de usuario
  const getAvailableAccounts = () => {
    if (isPortalUser) {
      // Usuario del portal: usar sus cuentas asignadas
      return (assignedAccounts || []).map(acc => ({
        account_number: acc.account_number,
        cvu: acc.cvu
      }))
    } else {
      // Operador: usar todas las cuentas
      return allAccountsForSelect
    }
  }

  const availableAccounts = getAvailableAccounts()

  // Seleccionar todas las cuentas
  const handleSelectAll = () => {
    if (selectedAccounts.length === availableAccounts.length) {
      // Si ya están todas seleccionadas, deseleccionar todas
      setSelectedAccounts([])
    } else {
      // Seleccionar todas
      setSelectedAccounts(availableAccounts.map(acc => acc.account_number))
    }
  }

  // Consultar saldos de múltiples cuentas
  const handleConsultarMultiplesSaldos = async (e) => {
    e.preventDefault()
    if (selectedAccounts.length === 0) {
      setError('Seleccione al menos una cuenta')
      return
    }

    setBalanceLoading(true)
    setError('')
    setMultipleBalances({})
    setLoadingProgress({ current: 0, total: selectedAccounts.length })

    // Inicializar todos como loading
    const initialBalances = {}
    selectedAccounts.forEach(accNum => {
      initialBalances[accNum] = { loading: true, balance: null, error: null }
    })
    setMultipleBalances(initialBalances)

    // Consultar en paralelo (máximo 5 a la vez para no saturar)
    const PARALLEL_REQUESTS = 5
    let completed = 0

    for (let i = 0; i < selectedAccounts.length; i += PARALLEL_REQUESTS) {
      const batch = selectedAccounts.slice(i, i + PARALLEL_REQUESTS)
      
      await Promise.all(batch.map(async (accNum) => {
        try {
          const response = await getBalance(accNum)
          setMultipleBalances(prev => ({
            ...prev,
            [accNum]: {
              loading: false,
              balance: response.data?.available_for_purchases || 0,
              error: response.description === 'blocked account' ? 'Bloqueada' : 
                     response.description === 'bad request' ? 'Error' : null
            }
          }))
        } catch (err) {
          setMultipleBalances(prev => ({
            ...prev,
            [accNum]: {
              loading: false,
              balance: null,
              error: 'Error'
            }
          }))
        } finally {
          completed++
          setLoadingProgress({ current: completed, total: selectedAccounts.length })
        }
      }))
    }

    setBalanceLoading(false)
  }

  // Obtener el CVU de una cuenta
  const getCvuForAccount = (accountNum) => {
    const acc = availableAccounts.find(a => a.account_number === accountNum)
    return acc?.cvu || '-'
  }

  // Calcular total de saldos
  const getTotalBalance = () => {
    return Object.values(multipleBalances).reduce((sum, item) => {
      return sum + (item.balance || 0)
    }, 0)
  }

  // Esperar a que el rol esté listo
  if (!roleReady) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando...</p>
      </div>
    )
  }

  // ============= RENDER CREDENCIAL =============
  if (userRole === 'CREDENCIAL') {
    // Si es usuario del portal sin cuentas asignadas
    if (isPortalUser && assignedAccounts.length === 0) {
      return (
        <div className="page-container">
          <div className="page-header">
            <h1>Balance</h1>
          </div>
          <div className="empty-state">
            <p className="empty-state-large">No tienes cuentas asignadas</p>
            <p>Contacta con el operador para que te asigne CVUs.</p>
          </div>
        </div>
      )
    }

    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Balance</h1>
        </div>

        <div className="balance-query-container">
          <div className="balance-query-card">
            <h2>Consultar Balance</h2>
            {/* Selector múltiple para TODOS los usuarios CREDENCIAL */}
            <form onSubmit={handleConsultarMultiplesSaldos} className="balance-form">
              <div className="form-group">
                <label>Seleccionar cuentas</label>
                <div className="multi-select-container">
                  <div 
                    className="multi-select-header"
                    onClick={() => !balanceLoading && !accountsSelectLoading && setShowAccountsDropdown(!showAccountsDropdown)}
                  >
                    <span>
                      {accountsSelectLoading ? 'Cargando cuentas...' : 
                       selectedAccounts.length === 0 ? 'Seleccione cuentas...' :
                       selectedAccounts.length === availableAccounts.length ? 'Todas las cuentas seleccionadas' :
                       `${selectedAccounts.length} cuenta(s) seleccionada(s)`}
                    </span>
                    <span className="dropdown-arrow">{showAccountsDropdown ? '▲' : '▼'}</span>
                  </div>
                  
                  {showAccountsDropdown && (
                    <div className="multi-select-dropdown">
                      <div 
                        className="multi-select-option select-all"
                        onClick={handleSelectAll}
                      >
                        <input 
                          type="checkbox" 
                          checked={selectedAccounts.length === availableAccounts.length && availableAccounts.length > 0}
                          readOnly
                        />
                        <span className="option-label-bold">Seleccionar todas ({availableAccounts.length})</span>
                      </div>
                      <div className="multi-select-divider"></div>
                      <div className="multi-select-options">
                        {availableAccounts.map((acc, index) => (
                          <div 
                            key={index}
                            className="multi-select-option"
                            onClick={() => handleToggleAccount(acc.account_number)}
                          >
                            <input 
                              type="checkbox" 
                              checked={selectedAccounts.includes(acc.account_number)}
                              readOnly
                            />
                            <span>{acc.account_number} - {acc.cvu}</span>
                          </div>
                        ))}
                      </div>
                      <div className="multi-select-divider"></div>
                      <div className="multi-select-footer">
                        <button 
                          type="button"
                          className="btn-confirm-selection"
                          onClick={() => setShowAccountsDropdown(false)}
                        >
                          Confirmar selección ({selectedAccounts.length})
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <button 
                type="submit" 
                className="btn-primary btn-block"
                disabled={balanceLoading || selectedAccounts.length === 0}
              >
                {balanceLoading ? (
                  <>
                    <div className="spinner-small"></div>
                    Consultando {loadingProgress.current} de {loadingProgress.total}...
                  </>
                ) : (
                  <>
                    <LuSearch /> Consultar Saldos
                  </>
                )}
              </button>
            </form>

            {error && <div className="error-message">{error}</div>}

            {/* Tabla de resultados para múltiples cuentas - para TODOS los usuarios CREDENCIAL */}
            {Object.keys(multipleBalances).length > 0 && (
              <div className="balance-crema">
                <div className="balance-band">
                  <p>Saldo total en ARS</p>
                  <strong>{formatCurrency(getTotalBalance())}</strong>
                </div>
                <div className="balance-columns">
                  {selectedAccounts.map((accNum) => {
                    const balanceInfo = multipleBalances[accNum] || {}
                    return (
                      <section key={accNum} className="balance-account">
                        <h2>{accNum}</h2>
                        <p className="balance-account-amount">
                          {balanceInfo.loading ? 'Cargando...' : balanceInfo.error ? balanceInfo.error : formatCurrency(balanceInfo.balance || 0)}
                        </p>
                        <p className="balance-account-cvu">{getCvuForAccount(accNum)}</p>
                        <Link className="btn-primary balance-transfer" to="/transfer">Transferir</Link>
                      </section>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal de cuenta bloqueada */}
        {showBlockedModal && (
          <div className="blocked-modal-overlay" onClick={() => setShowBlockedModal(false)}>
            <div className="blocked-modal" onClick={(e) => e.stopPropagation()}>
              <div className="blocked-modal-header">
                <h3>Cuenta Bloqueada</h3>
                <button onClick={() => setShowBlockedModal(false)} className="blocked-modal-close">
                  <LuX />
                </button>
              </div>
              <div className="blocked-modal-content">
                <p>LA CUENTA SE ENCUENTRA BLOQUEADA</p>
              </div>
              <div className="blocked-modal-actions">
                <button 
                  onClick={() => setShowBlockedModal(false)}
                  className="btn-primary btn-block"
                >
                  ACEPTAR
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de bad request */}
        {showBadRequestModal && (
          <div className="blocked-modal-overlay" onClick={() => setShowBadRequestModal(false)}>
            <div className="blocked-modal" onClick={(e) => e.stopPropagation()}>
              <div className="blocked-modal-header">
                <h3>Error</h3>
                <button onClick={() => setShowBadRequestModal(false)} className="blocked-modal-close">
                  <LuX />
                </button>
              </div>
              <div className="blocked-modal-content">
                <p>Error Bad Request, por favor consultar al administrador</p>
              </div>
              <div className="blocked-modal-actions">
                <button 
                  onClick={() => setShowBadRequestModal(false)}
                  className="btn-primary btn-block"
                >
                  ACEPTAR
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ============= RENDER BIND =============
  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando cuentas</p>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><LuWallet /> Mis Cuentas</h1>
        <div className="actions">
          <div className="export-dropdown">
            <button 
              onClick={() => setShowExportMenu(!showExportMenu)} 
              className="btn-secondary"
            >
              <LuDownload /> Exportar
            </button>
            {showExportMenu && (
              <div className="dropdown-menu">
                <button onClick={handleExportCSV} className="dropdown-item">
                  <LuFileSpreadsheet /> CSV
                </button>
                <button onClick={handleExportExcel} className="dropdown-item">
                  <LuSheet /> Excel
                </button>
                <button onClick={handleExportPDF} className="dropdown-item">
                  <LuFileText /> PDF
                </button>
                <button onClick={handlePrint} className="dropdown-item">
                  <LuPrinter /> Imprimir
                </button>
              </div>
            )}
          </div>
          <button onClick={loadAccounts} className="btn-primary">
            <LuRefreshCw /> Actualizar
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {!error && accounts.length === 0 && (
        <div className="empty-state">
          <p className="empty-state-large">No se encontraron cuentas</p>
        </div>
      )}

      {accounts.length > 0 && (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Alias</th>
                  <th>Número</th>
                  <th>Tipo</th>
                  <th>Saldo</th>
                  <th>CBU</th>
                  <th>Estado</th>
                  <th>Propietario</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.id}>
                    <td className="text-bold">
                      {account.label || '-'}
                    </td>
                    <td>{account.number}</td>
                    <td>{account.type}</td>
                    <td className="text-amount">
                      {formatCurrency(account.balance.amount)}
                    </td>
                    <td className="text-mono">{account.account_routing?.address || '-'}</td>
                    <td>
                      <span className={`badge badge-${account.status.toLowerCase()}`}>
                        {account.status}
                      </span>
                    </td>
                    <td>{account.owners?.[0]?.display_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="summary">
            <p>Total de cuentas: <strong>{accounts.length}</strong></p>
            <p>Saldo total: <strong>
              {formatCurrency(accounts.reduce((sum, acc) => sum + acc.balance.amount, 0))}
            </strong></p>
          </div>
        </>
      )}

      {/* Sección de CVUs dados de alta (BIND) */}
      <div className="cvus-section">
        <div className="cvus-header">
          <h2><LuIdCard /> Mis CVUs (CVUs dados de alta)</h2>
          <button onClick={loadCVUs} className="btn-secondary btn-small" disabled={cvusLoading}>
            <LuRefreshCw className={cvusLoading ? 'spinning' : ''} /> Actualizar
          </button>
        </div>

        {cvusLoading ? (
          <div className="cvus-loading">
            <div className="spinner-small"></div>
            <span>Cargando CVUs...</span>
          </div>
        ) : bindCVUs.length === 0 ? (
          <div className="cvus-empty">
            <p>No hay CVUs dados de alta</p>
            <p className="cvus-empty-hint">Los CVUs aparecerán aquí cuando los des de alta desde "Alta CVU"</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="data-table cvus-table">
                <thead>
                  <tr>
                    <th>CVU</th>
                    <th>Nombre</th>
                    <th>CUIT</th>
                    <th>Fecha de Alta</th>
                  </tr>
                </thead>
                <tbody>
                  {bindCVUs.map((cvu) => (
                    <tr key={cvu.id}>
                      <td className="text-mono">{cvu.cvu}</td>
                      <td>{cvu.name || '-'}</td>
                      <td>{cvu.cuit || '-'}</td>
                      <td>{new Date(cvu.created_at).toLocaleDateString('es-AR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="cvus-summary">
              <p>Total CVUs dados de alta: <strong>{bindCVUs.length}</strong></p>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AccountsPage
